import { ReactNode, PropsWithChildren } from 'react';
import { filterObject } from '../01-core/Utilities';
import { OptionalLink } from '../01-core/OptionalLink';
import { Media, MediaProps } from '../02-atoms/Media';
import { hasMedia } from '../02-atoms/Media.utils';
import { Info, InfoItemProps } from '../02-atoms/Info';
import { Icon } from '../02-atoms/Icon';
import { Action, ActionProps } from '../02-atoms/Action';
import { Button, ButtonProps } from '../02-atoms/Button';
import { TagProps } from '../02-atoms/Tag';
import { ButtonGroup } from './ButtonGroup';
import { Avatar, AvatarProps } from './Avatar';
import { Tags } from './Tags';

export interface CardProps
  extends PropsWithChildren,
    Pick<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      'target' | 'rel' | 'role'
    > {
  /** Type of card */
  type:
    | 'base'
    | 'product'
    | 'person'
    | 'event'
    | 'news'
    | 'school'
    | 'course'
    | 'training'
    | 'publication'
    | 'researchfield'
    | 'service'
    | 'topic'
    | 'researchfocus'
    | 'specialization'
    | 'teaser'
    | 'teaser-school'
    | 'teaser-fullbleed'
    | 'testimonial'
    | 'testimonial-prominent'
    | 'project'
    | 'register'
    | 'panel'
    | 'panel-alternative'
    | 'search';
  /** Stable identity of the underlying content, used as React key in listings.
   * Required because `link` is not unique: every Occasion of an event carries
   * the link of its parent event. */
  uid?: string;
  /** Style of card */
  style?: 'tile' | 'list' | 'table';
  /** Category of card */
  category?: string;
  /** Title of card */
  title: string | ReactNode;
  /** Heading level of the card title (defaults to h4) */
  titleTag?: 'h4' | 'h5';
  /** Sub-title of card */
  subtitle?: string | ReactNode;
  /** Description of card */
  description?: string | ReactNode;
  /** Bulletpoints of card */
  bulletpoints?: string[];
  /** Info on card */
  info?: InfoItemProps[];
  /** Actions on card (list of buttons) */
  actions?: ButtonProps[];
  /** Tags on card */
  tags?: TagProps[];
  /** Link on card */
  link?: string;
  /** Image */
  image?: MediaProps;
  /** Keep the image aspect as given instead of normalizing it per card type */
  keepImageAspect?: boolean;
  /** Memorize action */
  memorize?: ActionProps;
  /** Read more button */
  readmore?: ButtonProps;
  /** Avatar */
  avatar?: AvatarProps;
  /** Icon on card */
  icon?: string;
  /** Additional class */
  className?: string;
  /** Additional class for link */
  linkClassName?: string;
  /** Optional click handler */
  onClick?: (e: MouseEvent) => void;
  [name: `aria-${string}`]: string | boolean;
}

/** A card */
export const Card = ({
  type,
  // Identity only — must not reach the DOM
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  uid,
  style,
  category,
  title,
  titleTag,
  subtitle,
  description,
  bulletpoints,
  info,
  actions,
  tags,
  link,
  image,
  keepImageAspect,
  memorize,
  readmore,
  avatar,
  icon,
  className,
  linkClassName,
  onClick,
  children,
  ...props
}: CardProps) => {
  const ariaProps = Object.fromEntries(
    Object.entries(props).filter(([k]) => k.startsWith('aria-')),
  );
  const info_type = style === 'table' ? 'table' : 'simple';
  const TitleTag = titleTag ?? 'h4';
  // With a title link present the image is rendered non-interactively and
  // the title link is stretched over the whole card via CSS, keeping the
  // card surface clickable with a single link and tab stop (#1574).
  const hasStretchedTitleLink = Boolean(title && (link || onClick));

  if (image && hasMedia(image)) {
    image = { ...image };
    if (type === 'teaser-school') {
      image.aspect = 'portrait';
    } else if (
      !keepImageAspect &&
      ((type !== 'news' && type !== 'project') || style !== 'tile')
    ) {
      image.aspect = 'landscape';
    }
  }

  if (
    type === 'teaser' ||
    type === 'teaser-fullbleed' ||
    type === 'teaser-school' ||
    type === 'panel' ||
    type === 'panel-alternative' ||
    type === 'testimonial' ||
    type === 'testimonial-prominent'
  ) {
    style = 'tile';
  }

  return (
    <article
      className={[
        'card',
        `card--${type}`,
        style ? `card--${style}` : '',
        className,
        style !== 'table' && hasMedia(image)
          ? `card--with-image card--${image?.aspect || 'landscape'}`
          : 'card--no-image card--landscape',
        style !== 'table' && avatar && hasMedia(avatar?.image)
          ? 'card--avatar-image'
          : 'card--no-avatar-image',
        hasStretchedTitleLink ? 'card--stretched-link' : '',
      ].join(' ')}
      {...filterObject(props, null, ['target', 'rel', 'role'], null)}
    >
      <div className="card__wrapper">
        {style !== 'table' &&
          hasMedia(image) &&
          (hasStretchedTitleLink ? (
            // The title link already announces the target, so render the
            // duplicated image area as a non-interactive, decorative block.
            <div
              className={`card__image ${linkClassName || ''}`}
              aria-hidden="true"
            >
              <Media {...image} />
            </div>
          ) : (
            <OptionalLink
              className={`card__image ${linkClassName || ''}`}
              button={true}
              div={true}
              href={link}
              onClick={onClick}
              {...filterObject(props, ['target', 'rel', 'role'], null)}
              {...ariaProps}
            >
              <Media {...image} />
            </OptionalLink>
          ))}
        <div className="card__content">
          {(category || (style !== 'table' && memorize)) && (
            <div className="card__header">
              {category && <div className="card__category">{category}</div>}
              {style !== 'table' && memorize && (
                <Action className="card__memorize" {...memorize} />
              )}
            </div>
          )}
          {title && (
            <TitleTag className="card__title">
              <OptionalLink
                button={true}
                div={true}
                href={link}
                onClick={onClick}
                className={['card__title-link', linkClassName]
                  .filter(Boolean)
                  .join(' ')}
                {...filterObject(props, ['target', 'rel', 'role'], null)}
                {...ariaProps}
              >
                {icon && <Icon className="card__icon" icon={icon} />}
                <span className="card__title-text">{title}</span>
              </OptionalLink>
              {subtitle && <small className="card__subtitle">{subtitle}</small>}
            </TitleTag>
          )}
          {style !== 'table' && description && (
            <div className="card__description">{description}</div>
          )}
          {children}
          {style !== 'table' && bulletpoints && (
            <ul className="card__bulletpoints">
              {bulletpoints.map((point, i) => {
                return <li key={i}>{point}</li>;
              })}
            </ul>
          )}
          {info && (
            <Info className="card__info" type={info_type} items={info} />
          )}
          {style !== 'table' && actions && (
            <ButtonGroup className="card__actions" items={actions} />
          )}
          {style !== 'table' && (tags || readmore) && (
            <div className="card__footer">
              {tags && <Tags className="card__tags" items={tags} />}
              {readmore && (
                <Button
                  className="card__readmore"
                  type="tertiary"
                  {...readmore}
                />
              )}
            </div>
          )}
          {style !== 'table' && avatar && (
            <Avatar className="card__avatar" {...avatar} />
          )}
        </div>
      </div>
    </article>
  );
};
